import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../assets/images/header-logo.png";
import whiteLogo from "../assets/images/header-white-logo.png";
import {
    Menu,
    X,
    Settings,
    LogOut,
    LogIn,
    Moon,
    Sun,
} from "lucide-react";

export default function Header({ user, setUser, onLoginClick }) {
    const [darkMode, setDarkMode] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [userOpen, setUserOpen] = useState(false);

    const location = useLocation();
    const isActive = (path) => location.pathname === path;

    useEffect(() => {
        if (darkMode) {
            document.documentElement.classList.add("dark");
        } else {
            document.documentElement.classList.remove("dark");
        }
    }, [darkMode]);

    const logout = () => {
        localStorage.removeItem("user");
        setUser(null);
    };

    const closeMobileMenu = () => setMenuOpen(false);

    const navLinkClass = (path) =>
        `transition font-medium hover:text-blue-600 ${isActive(path)
            ? "text-blue-600 border-b-2 border-blue-600 pb-1"
            : "text-gray-700 dark:text-gray-200"
        }`;

    const mobileNavClass = (path) =>
        `px-4 py-3 rounded-xl transition ${isActive(path)
            ? "bg-blue-600 text-white"
            : "hover:bg-blue-50 hover:text-blue-600 text-gray-700 dark:text-gray-200"
        }`;

    return (
        <header className="sticky top-0 z-50 bg-white dark:bg-gray-900 dark:text-white shadow-md py-2">
            <div className="container mx-auto px-6">
                <div className="flex items-center justify-between h-20">

                    {/* LOGO */}

                    <Link to="/" className="flex items-center gap-3">
                        <img
                            src={darkMode ? whiteLogo : logo}
                            alt="Discounted Hotels"
                            className="h-[50px] sm:h-[60px] xl:h-[75px] w-auto object-contain"
                        />

                    </Link>

                    {/* NAV */}
                    <nav className="hidden md:flex items-center gap-6">
                        <Link to="/" className={navLinkClass("/")}>Home</Link>
                        <Link to="/hotels" className={navLinkClass("/hotels")}>Hotels</Link>
                        <Link to="/about-us" className={navLinkClass("/about-us")}>About Us</Link>
                        <Link to="/services" className={navLinkClass("/services")}>Services</Link>
                        <Link to="/contact-us" className={navLinkClass("/contact-us")}>Contact Us</Link>
                        <Link to="/blogs" className={navLinkClass("/blogs")}>Blogs</Link>
                    </nav>

                    {/* ACTIONS */}
                    <div className="hidden md:flex items-center gap-3">

                        {/* DARK MODE */}
                        <button
                            onClick={() => setDarkMode(!darkMode)}
                            className="
        flex items-center gap-2
        px-3 py-2 rounded-lg
        hover:bg-gray-100 dark:hover:bg-gray-800
        transition
    "
                        >
                            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
                            <span className="text-sm">
                                {darkMode ? "Light" : "Dark"}
                            </span>
                        </button>



                        {/* LOGIN / USER */}
                        {!user ? (
                            <button
                                onClick={onLoginClick}
                                className="flex items-center gap-2 px-4 py-2 rounded-xl 
                                bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-700 
                                text-white font-medium hover:scale-105 transition"
                            >
                                <LogIn size={18} />
                                Login
                            </button>
                        ) : (
                            <div className="relative">
                                <button
                                    onClick={() => setUserOpen(!userOpen)}
                                    className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                                >
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-700 text-white flex items-center justify-center font-semibold">
                                        {user?.name?.charAt(0)}
                                    </div>
                                    <span className="hidden sm:block">
                                        {user?.name?.split(" ")[0]}
                                    </span>
                                </button>

                                {userOpen && (
                                    <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-gray-800 shadow-xl rounded-xl py-2">

                                        <Link
                                            to="/profile"
                                            onClick={() => setUserOpen(false)}
                                            className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        >
                                            <Settings size={16} /> Profile
                                        </Link>



                                        <button
                                            onClick={() => {
                                                setUserOpen(false);
                                                logout();
                                            }}
                                            className="w-full text-left flex items-center gap-2 px-4 py-2 text-red-500 hover:bg-red-50"
                                        >
                                            <LogOut size={16} />
                                            Logout
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* MOBILE BUTTON */}
                    <div className="md:hidden flex items-center gap-3">

                        <button
                            onClick={() => setDarkMode(!darkMode)}
                            className="
        flex items-center gap-2
        px-3 py-2
        rounded-xl
        bg-gray-100 dark:bg-gray-800
        hover:bg-blue-50 dark:hover:bg-gray-700
        transition
    "
                        >
                            {darkMode ? <Sun size={18} /> : <Moon size={18} />}

                            <span className="text-sm font-medium">
                                {darkMode ? "Light" : "Dark"}
                            </span>
                        </button>

                        {!user ? (
                            <button
                                onClick={onLoginClick}
                                className="flex items-center gap-1 text-blue-600 font-medium"
                            >
                                <LogIn size={18} />
                                <span>Login</span>
                            </button>
                        ) : (
                            <div className="relative">

                                <button
                                    onClick={() => setUserOpen(!userOpen)}
                                    className="flex items-center gap-2"
                                >
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-700 text-white flex items-center justify-center font-semibold text-sm">
                                        {user?.name?.charAt(0)}
                                    </div>

                                    <span className="max-w-[80px] truncate text-sm font-medium">
                                        {user?.name?.split(" ")[0]}
                                    </span>
                                </button>

                                {userOpen && (
                                    <div className="absolute right-0 top-12 w-52 bg-white dark:bg-gray-800 shadow-xl rounded-xl py-2 z-50">

                                        <Link
                                            to="/profile"
                                            onClick={() => setUserOpen(false)}
                                            className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        >
                                            <Settings size={16} />
                                            Profile
                                        </Link>

                                        <button
                                            onClick={() => {
                                                setUserOpen(false);
                                                logout();
                                            }}
                                            className="w-full text-left flex items-center gap-2 px-4 py-2 text-red-500 hover:bg-red-50"
                                        >
                                            <LogOut size={16} />
                                            Logout
                                        </button>

                                    </div>
                                )}

                            </div>
                        )}

                        <button onClick={() => setMenuOpen(true)}>
                            <Menu size={24} />
                        </button>

                    </div>
                </div>

                {/* MOBILE MENU */}
                <>
                    {/* Overlay */}
                    <div
                        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 md:hidden ${menuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
                            }`}
                        onClick={() => setMenuOpen(false)}
                    />

                    {/* Slide Menu */}
                    <div
                        className={`fixed top-0 left-0 w-full bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 shadow-2xl z-50 rounded-b-3xl transform transition-transform duration-300 ease-in-out md:hidden ${menuOpen ? "translate-x-0" : "-translate-x-full"
                            }`}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-5 border-b">

                            <Link to="/" className="flex items-center gap-3">
                                <img
                                    src={darkMode ? whiteLogo : logo}
                                    alt="Discounted Hotels"
                                    className="h-[50px] sm:h-[60px] xl:h-[75px] w-auto object-contain"
                                />

                            </Link>

                            <button onClick={() => setMenuOpen(false)}>
                                <X size={24} />
                            </button>

                        </div>

                        {/* Menu Links */}
                        <nav className="flex flex-col p-5 gap-2">

                            <Link to="/" onClick={closeMobileMenu} className={mobileNavClass("/")}>
                                Home
                            </Link>

                            <Link to="/hotels" onClick={closeMobileMenu} className={mobileNavClass("/hotels")}>
                                Hotels
                            </Link>

                            <Link to="/about-us" onClick={closeMobileMenu} className={mobileNavClass("/about-us")}>
                                About Us
                            </Link>

                            <Link to="/services" onClick={closeMobileMenu} className={mobileNavClass("/services")}>
                                Services
                            </Link>

                            <Link to="/contact-us" onClick={closeMobileMenu} className={mobileNavClass("/contact-us")}>
                                Contact Us
                            </Link>

                            <Link to="/blogs" onClick={closeMobileMenu} className={mobileNavClass("/blogs")}>
                                Blogs
                            </Link>

                        </nav>


                    </div>
                </>
            </div>
        </header>
    );
}

